import { HOST_PRESENTS, profileBlocks } from "@/lib/profile-template";
import type { ProfileHostPresents, PublicProfile } from "@/lib/profile-template";
import type { CSSProperties } from "react";

// The styles read the design's own tokens when this block is painted inside the design surface (the
// export defines the `--sg-*` variables on its root) and fall back to the template palette elsewhere,
// so the same component reads as one more design section under PRODUCTS and as a plain section on a
// bare template.
const section: CSSProperties = {
  padding: "22px 0 0",
  borderTop: "1px solid var(--sg-grey-100, rgba(10,31,68,.12))",
};
const heading: CSSProperties = {
  margin: "0 0 12px",
  fontFamily: "var(--sg-font-display, inherit)",
  fontSize: 24,
  fontWeight: 400,
  textTransform: "uppercase",
  color: "var(--sg-ink-900, #0A1F44)",
};
const body: CSSProperties = {
  margin: "0 0 10px",
  color: "var(--sg-grey-600, #334155)",
  lineHeight: 1.55,
  fontSize: 14.5,
};
const eyebrow: CSSProperties = {
  margin: 0,
  fontFamily: "var(--sg-font-secondary, inherit)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--sg-grey-500, #64748B)",
};

/**
 * Shared informational profile surface. It never renders, prices, or mutates purchasable listings.
 *
 * `host` is what the mounting surface already presents on its own (see `HOST_PRESENTS`); the
 * component renders the complement, so a fact is on the page once. The hierarchy line and Reviews
 * always render — no host carries them. `data-profile-presented` records the declaration in the
 * DOM so an instrument can read which facts were left to the host.
 */
export default function PublicProfileSections({
  profile,
  host = HOST_PRESENTS.nothing,
}: {
  profile: PublicProfile;
  host?: ProfileHostPresents;
}) {
  const blocks = profileBlocks(profile, host);
  const presented = (["about", "contact", "sections"] as const)
    .filter((key) => host[key])
    .join(" ");
  const hasContact = !!profile.contact.phone || !!profile.contact.email;
  return (
    <aside
      data-public-profile={profile.entityKey}
      data-profile-kind={profile.kind}
      {...(presented ? { "data-profile-presented": presented } : {})}
      style={{ maxWidth: 860 }}
    >
      {blocks.hierarchy && profile.parent ? (
        <p data-profile-hierarchy style={eyebrow}>
          {/* One string, not two text nodes: SSR would otherwise emit `Part of <!-- -->Whistler`,
              and the raw-HTML gate reads the literal phrase. */}
          {`Part of ${profile.parent.name}`}
        </p>
      ) : null}
      {blocks.about ? (
        <section style={section} data-profile-about>
          <h2 style={heading}>About</h2>
          {profile.about.slice(0, 2).map((line) => (
            <p key={line} style={body}>
              {line}
            </p>
          ))}
        </section>
      ) : null}
      {blocks.contact ? (
        <section style={section} data-profile-contact>
          <h2 style={heading}>Contact</h2>
          {hasContact ? (
            <p style={body}>
              {profile.contact.phone ? (
                <>
                  <a href={`tel:${profile.contact.phone.replace(/[^+\d]/g, "")}`}>
                    {profile.contact.phone}
                  </a>
                  {profile.contact.email ? " · " : null}
                </>
              ) : null}
              {profile.contact.email ? (
                <a href={`mailto:${profile.contact.email}`}>
                  {profile.contact.email}
                </a>
              ) : null}
            </p>
          ) : (
            <p data-profile-contact-empty style={body}>
              Contact details have not been published for this profile.
            </p>
          )}
        </section>
      ) : null}
      <section style={section} data-profile-reviews>
        <h2 style={heading}>Reviews</h2>
        {profile.reviews.length ? (
          profile.reviews.map((review) => (
            <p key={review} style={body}>
              {review}
            </p>
          ))
        ) : (
          <p data-profile-reviews-empty style={body}>
            No published reviews yet.
          </p>
        )}
      </section>
      {blocks.sections
        ? profile.sections.slice(0, 2).map((item) => (
            <section key={item.title} style={section}>
              <h2 style={heading}>{item.title}</h2>
              {item.body.slice(0, 2).map((line) => (
                <p key={line} style={body}>
                  {line}
                </p>
              ))}
            </section>
          ))
        : null}
    </aside>
  );
}
